import React, { useCallback, useEffect, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import { Link } from "react-router-dom";
import "../css/chat-ai-sidebar.scss";
import {
  getStoredMessages,
  getStoredSessionId,
  isChatMarkedFinished,
  postWebChat,
  setChatFinished,
  setStoredMessages,
  setStoredSessionId,
} from "../service/chatAiApi";

const WELCOME =
  "Сайн байна уу? Hello?";
const WELCOME_MESSAGE = { id: "welcome", role: "assistant", text: WELCOME, links: [] };

function stripLinksFromText(text, links) {
  if (typeof text !== "string" || !Array.isArray(links) || links.length === 0) {
    return text;
  }

  const escapedLinks = links
    .filter((url) => typeof url === "string" && url.trim().length > 0)
    .map((url) => url.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));

  if (escapedLinks.length === 0) {
    return text;
  }

  const linkRegex = new RegExp(`\\b(?:${escapedLinks.join("|")})\\b`, "g");
  return text
    .split(/\r?\n/)
    .map((line) => line.replace(linkRegex, "").trimEnd())
    .filter((line, index, arr) => line.length > 0 || (index > 0 && arr[index - 1].length > 0))
    .join("\n")
    .trim();
}

function normalizeLinks(links) {
  if (!Array.isArray(links)) {
    return [];
  }

  return Array.from(
    new Set(links.filter((url) => typeof url === "string" && url.trim().length > 0))
  );
}

function parseStoreLink(url) {
  if (typeof url !== "string") {
    return null;
  }

  if (url.includes("play.google.com/store/apps/details?id=gobicraft.tomko")) {
    return { app: "Tomko (Language Learning App)", store: "Google Play Store", url };
  }

  if (
    url.includes("apps.apple.com") &&
    (url.includes("/tomko/id6749509851") || url.includes("/id6749509851"))
  ) {
    return { app: "Tomko (Language Learning App)", store: "Apple App Store", url };
  }

  if (url.includes("play.google.com/store/apps/details?id=gobicraft.tomkobooks")) {
    return { app: "Tomko Books (Digital Reading App)", store: "Google Play Store", url };
  }

  if (
    url.includes("apps.apple.com") &&
    (url.includes("/tomko-books/id6749153308") || url.includes("/id6749153308"))
  ) {
    return { app: "Tomko Books (Digital Reading App)", store: "Apple App Store", url };
  }

  return null;
}

function renderAssistantLinks(messageId, links) {
  const parsedLinks = links.map(parseStoreLink);
  const allKnown = parsedLinks.every((item) => item !== null);

  if (!allKnown) {
    return (
      <div className="chat-ai-sidebar__links">
        {links.map((url) => (
          <a
            key={`${messageId}-${url}`}
            href={url}
            className="chat-ai-sidebar__link"
            target="_blank"
            rel="noreferrer"
          >
            {url}
          </a>
        ))}
      </div>
    );
  }

  const grouped = parsedLinks.reduce((accumulator, item) => {
    const appKey = item.app;
    if (!accumulator[appKey]) {
      accumulator[appKey] = [];
    }
    accumulator[appKey].push(item);
    return accumulator;
  }, {});

  return (
    <div className="chat-ai-sidebar__bubble-text">
      {Object.entries(grouped).map(([appName, appLinks]) => (
        <div key={`${messageId}-${appName}`}>
          <p>{appName}</p>
          {appLinks.map((entry) => (
            <React.Fragment key={`${messageId}-${entry.url}`}>
              <p>{entry.store}:</p>
              <a
                href={entry.url}
                className="chat-ai-sidebar__link"
                target="_blank"
                rel="noreferrer"
              >
                {entry.url}
              </a>
            </React.Fragment>
          ))}
        </div>
      ))}
    </div>
  );
}

const defaultChatAiCopy = {
  fabLabel: "AI Assistant",
  fabAriaOpen: "Open assistant",
  fabAriaClose: "Close assistant",
};

const ChatAiSidebar = ({ data: chatAiCopy }) => {
  const copy = { ...defaultChatAiCopy, ...chatAiCopy };
  const finished = isChatMarkedFinished();
  const storedMessages = getStoredMessages();
  const storedSessionId = getStoredSessionId();
  const hasUnfinishedChat =
    !finished && (storedMessages.length > 0 || Boolean(storedSessionId));

  const [open, setOpen] = useState(() => hasUnfinishedChat);
  const [messages, setMessages] = useState(() => {
    if (finished) {
      return [WELCOME_MESSAGE];
    }
    return storedMessages.length > 0 ? storedMessages : [WELCOME_MESSAGE];
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(() => (finished ? "" : storedSessionId));
  const [fabHidden, setFabHidden] = useState(false);
  const listEndRef = useRef(null);
  const inputRef = useRef(null);

  const configured = Boolean(
    process.env.REACT_APP_CHAT_AI_API_URL && process.env.REACT_APP_CHAT_AI_API_TOKEN
  );

  useEffect(() => {
    if (open) {
      listEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open, loading]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useEffect(() => {
    const nonWelcomeMessages = messages.filter((message) => message.id !== "welcome");
    setStoredMessages(nonWelcomeMessages);
  }, [messages]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading || !configured) {
      return;
    }

    setError(null);
    setInput("");

    const userMessage = {
      id: `u-${Date.now()}`,
      role: "user",
      text,
    };
    setMessages((current) => [...current, userMessage]);
    setLoading(true);

    try {
      const { reply, links, sessionId: nextSessionId } = await postWebChat({
        message: text,
        sessionId: sessionId || undefined,
      });

      setSessionId(nextSessionId);
      setStoredSessionId(nextSessionId);
      setChatFinished(false);
      setMessages((current) => [
        ...current,
        { id: `a-${Date.now()}`, role: "assistant", text: reply, links },
      ]);
    } catch (requestError) {
      const message =
        requestError instanceof Error ? requestError.message : "Something went wrong.";
      setError(message);
      setMessages((current) => current.filter((item) => item.id !== userMessage.id));
      setInput(text);
    } finally {
      setLoading(false);
    }
  }, [configured, input, loading, sessionId]);

  const newChat = useCallback(() => {
    setChatFinished(false);
    setSessionId("");
    setStoredSessionId("");
    setMessages([WELCOME_MESSAGE]);
    setError(null);
    setInput("");
  }, []);

  const finishChat = useCallback(() => {
    setChatFinished(true);
    setSessionId("");
    setMessages([WELCOME_MESSAGE]);
    setError(null);
    setInput("");
    setOpen(false);
  }, []);

  const onSubmit = (event) => {
    event.preventDefault();
    send();
  };

  if (!configured) {
    return null;
  }

  return (
    <>
      {!open && !fabHidden && (
        <div className="chat-ai-sidebar__fab-wrap">
          <button
            type="button"
            className="chat-ai-sidebar__fab"
            aria-expanded={open}
            aria-controls="chat-ai-sidebar-panel"
            aria-label={copy.fabAriaOpen}
            onClick={() => setOpen(true)}
          >
            <span className="chat-ai-sidebar__fab-label">{copy.fabLabel}</span>
          </button>
          <button
            type="button"
            className="chat-ai-sidebar__fab-close"
            aria-label={copy.fabAriaClose}
            onClick={() => setFabHidden(true)}
          >
            <IoClose aria-hidden="true" />
          </button>
        </div>
      )}

      {open && (
        <div
          className="chat-ai-sidebar__backdrop"
          aria-hidden="true"
          onClick={() => setOpen(false)}
        />
      )}

      <aside
        id="chat-ai-sidebar-panel"
        className={`chat-ai-sidebar${open ? " chat-ai-sidebar--open" : ""}`}
        aria-hidden={!open}
      >
        <header className="chat-ai-sidebar__header">
          <div>
            <h2 className="chat-ai-sidebar__title">AI Agent</h2>
            <p className="chat-ai-sidebar__subtitle">Gobicraft chat agent v1</p>
          </div>
          <div className="chat-ai-sidebar__header-actions">
            <button
              type="button"
              className="chat-ai-sidebar__ghost-btn"
              onClick={newChat}
              disabled={loading}
            >
              New chat
            </button>
            <button
              type="button"
              className="chat-ai-sidebar__icon-btn"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >
              x
            </button>
          </div>
        </header>

        <div className="chat-ai-sidebar__messages" role="log" aria-live="polite">
          {messages.map((message) => {
            const links = normalizeLinks(message.links);
            const cleanedText =
              message.role === "assistant"
                ? stripLinksFromText(message.text, links)
                : message.text;

            return (
              <div
                key={message.id}
                className={`chat-ai-sidebar__bubble chat-ai-sidebar__bubble--${message.role}`}
              >
                {cleanedText && <p className="chat-ai-sidebar__bubble-text">{cleanedText}</p>}
                {message.role === "assistant" &&
                  links.length > 0 &&
                  renderAssistantLinks(message.id, links)}
              </div>
            );
          })}
          {loading && (
            <div className="chat-ai-sidebar__bubble chat-ai-sidebar__bubble--assistant chat-ai-sidebar__typing">
              <span />
              <span />
              <span />
            </div>
          )}
          <div ref={listEndRef} />
        </div>

        {error && <div className="chat-ai-sidebar__banner chat-ai-sidebar__banner--err">{error}</div>}

        <form className="chat-ai-sidebar__composer" onSubmit={onSubmit}>
          <textarea
            ref={inputRef}
            className="chat-ai-sidebar__input"
            rows={2}
            placeholder="Message..."
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
            disabled={!configured}
            maxLength={8000}
          />
          <button
            type="submit"
            className="chat-ai-sidebar__send"
            disabled={loading || !input.trim()}
          >
            Send
          </button>
        </form>

        <button
          type="button"
          className="chat-ai-sidebar__finish-btn"
          onClick={finishChat}
          disabled={loading}
        >
          Finish chat
        </button>

        <footer className="chat-ai-sidebar__footer">
          <Link to="/privacy">Privacy</Link>
          <span aria-hidden="true"> | </span>
          <Link to="/ai-terms">Terms</Link>
        </footer>
      </aside>
    </>
  );
};

export default ChatAiSidebar;
